import type { AxisAssessment, LevelAssessmentResult } from "@shared/labs/level-assessment";
import type { AxisReviewDecision } from "@shared/labs/level-assessment-calibration";
import { Check, Pencil, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { C, DM, levelLabel, UI } from "./ui-tokens";

/** Context the page threads in so the review can be attributed + replayed. */
export interface ReviewContext {
  modelId: string;
  disciplineId: string | null;
  /** Persisted run id — lets the review reuse the profile snapshot captured at
   *  run time instead of re-fetching (preferred; this is how a shared link's
   *  review works). */
  evaluationId?: string;
  /** Source URL of the evaluated profile — the re-fetch fallback used when no
   *  `evaluationId` is available. */
  linkedinUrl?: string;
  candidateName: string | null;
  /** Evaluation set this review originated from — provenance only (samples stay
   *  `source = 'review'`). Set when reviewing from the multi-candidate surface;
   *  omitted for a single-evaluation review. */
  evaluationSetId?: string;
}

/** Per-axis review state. `null` = not yet reviewed (nothing collected). */
type AxisVerdict = { agreed: true } | { agreed: false; correctedLevel: number } | null;

interface AxisRow {
  axisId: string;
  scope: "cross" | "vertical";
  label: string;
  modelLevel: number | null;
  modelConfidence: number;
}

function buildRows(result: LevelAssessmentResult): AxisRow[] {
  const labelById = new Map<string, string>();
  for (const a of result.modelSnapshot.crossAxes) labelById.set(a.id, a.label);
  for (const a of result.modelSnapshot.discipline?.axes ?? []) labelById.set(a.id, a.label);
  const fromSection = (a: AxisAssessment): AxisRow => ({
    axisId: a.axis,
    scope: a.scope,
    label: labelById.get(a.axis) ?? a.axis,
    modelLevel: a.inferredLevel,
    modelConfidence: a.confidence,
  });
  return [...result.cross.axes.map(fromSection), ...result.vertical.axes.map(fromSection)];
}

/**
 * End-of-evaluation review. The operator confirms or corrects each axis score
 * the model produced; every reviewed axis becomes one calibration sample.
 * Nothing is collected implicitly — only axes explicitly confirmed or
 * corrected are submitted, and the submit count makes the unreviewed remainder
 * visible.
 */
export function ReviewPanel({
  result,
  reviewContext,
  onClose,
  onSaved,
}: {
  result: LevelAssessmentResult;
  reviewContext: ReviewContext;
  onClose: () => void;
  /** Called after a successful save (every reviewed competency persisted as a
   *  sample). Optional — the results screen doesn't need it; the guided
   *  calibration workflow uses it to advance to the next profile. */
  onSaved?: (savedCount: number) => void;
}) {
  const { hasLabsAccess } = useAuth();
  const { toast } = useToast();
  const rows = useMemo(() => buildRows(result), [result]);
  const levels = result.modelSnapshot.levels;
  const levelOptions = useMemo(() => {
    const out: number[] = [];
    for (let l = levels.min; l <= levels.max; l++) out.push(l);
    return out;
  }, [levels.min, levels.max]);

  const [verdicts, setVerdicts] = useState<Record<string, AxisVerdict>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Saving a review writes calibration samples (shared model state) — a labs/DS
  // action, not part of the open "use" surface. Hide it from platform users who
  // lack the labs grant. See docs/labs/level-assessment/access-model.md.
  if (!hasLabsAccess) return null;

  const keyOf = (r: AxisRow) => `${r.scope}:${r.axisId}`;
  const reviewedCount = rows.filter((r) => verdicts[keyOf(r)] != null).length;

  const setConfirm = (r: AxisRow) => setVerdicts((v) => ({ ...v, [keyOf(r)]: { agreed: true } }));
  const setCorrect = (r: AxisRow, level: number) =>
    setVerdicts((v) => ({ ...v, [keyOf(r)]: { agreed: false, correctedLevel: level } }));
  const clear = (r: AxisRow) => setVerdicts((v) => ({ ...v, [keyOf(r)]: null }));

  async function submit() {
    const decisions: AxisReviewDecision[] = [];
    for (const r of rows) {
      const verdict = verdicts[keyOf(r)];
      if (verdict == null) continue;
      decisions.push({
        axisId: r.axisId,
        scope: r.scope,
        modelLevel: r.modelLevel,
        modelConfidence: r.modelConfidence,
        agreed: verdict.agreed,
        correctedLevel: verdict.agreed ? (r.modelLevel as number) : verdict.correctedLevel,
      });
    }
    if (decisions.length === 0) {
      toast({
        title: "Nada para salvar",
        description: "Confirme ou corrija ao menos uma competência.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentAxisReviewSave({
        modelId: reviewContext.modelId,
        disciplineId: reviewContext.disciplineId,
        evaluationId: reviewContext.evaluationId,
        linkedinUrl: reviewContext.linkedinUrl,
        candidateName: reviewContext.candidateName,
        evaluationSetId: reviewContext.evaluationSetId,
        decisions,
      })) as { saved: number };
      toast({
        title: "Revisão salva",
        description: `${res.saved} ${res.saved === 1 ? "amostra coletada" : "amostras coletadas"}.`,
      });
      setDone(true);
      onSaved?.(res.saved);
    } catch (e) {
      toast({
        title: "Falha ao salvar revisão",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="review-panel"
      style={{
        border: `1px solid ${C.ink20}`,
        borderRadius: 14,
        padding: 18,
        background: C.white,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ ...DM, fontWeight: 700, fontSize: 15, color: C.ink }}>
            Revisar avaliação
          </div>
          <div style={{ ...UI, fontSize: 12.5, color: C.ink50, marginTop: 2 }}>
            Confirme ou corrija cada competência. Só as revisadas viram amostra de calibração.
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="review-close"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: C.ink50,
            display: "inline-flex",
          }}
          aria-label="Fechar revisão"
        >
          <X size={18} />
        </button>
      </div>

      {done ? (
        <div style={{ ...UI, fontSize: 13.5, color: C.ink70, padding: "6px 2px" }}>
          Revisão registrada. Você pode fechar este painel.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((r) => {
              const verdict = verdicts[keyOf(r)];
              const abstained = r.modelLevel === null;
              return (
                <div
                  key={keyOf(r)}
                  data-testid={`review-row-${r.axisId}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: verdict != null ? C.ink5 : C.paper,
                    border: `1px solid ${verdict != null ? C.ink20 : "transparent"}`,
                  }}
                >
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <div style={{ ...DM, fontSize: 13.5, fontWeight: 600, color: C.ink }}>
                      {r.label}
                      <span
                        style={{
                          ...UI,
                          fontWeight: 500,
                          fontSize: 11,
                          color: C.ink50,
                          marginLeft: 6,
                        }}
                      >
                        {r.scope === "cross" ? "cross" : "vertical"}
                      </span>
                    </div>
                    <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 1 }}>
                      Modelo:{" "}
                      {abstained ? "abstenção (N/D)" : levelLabel(r.modelLevel, levels.names)} ·{" "}
                      {Math.round(r.modelConfidence * 100)}% confiança
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <VerdictButton
                      active={verdict?.agreed === true}
                      disabled={abstained}
                      onClick={() => setConfirm(r)}
                      testId={`review-confirm-${r.axisId}`}
                      icon={Check}
                      title={abstained ? "Modelo se absteve — corrija com um nível" : undefined}
                    >
                      Concordo
                    </VerdictButton>
                    <VerdictButton
                      active={verdict?.agreed === false}
                      onClick={() => {
                        // Open correction mode with a sensible default level.
                        setCorrect(
                          r,
                          verdict?.agreed === false
                            ? verdict.correctedLevel
                            : (r.modelLevel ?? levels.min),
                        );
                      }}
                      testId={`review-correct-${r.axisId}`}
                      icon={Pencil}
                    >
                      Corrigir
                    </VerdictButton>
                    {verdict != null && (
                      <button
                        type="button"
                        onClick={() => clear(r)}
                        data-testid={`review-clear-${r.axisId}`}
                        style={{
                          ...UI,
                          fontSize: 11.5,
                          color: C.ink50,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        limpar
                      </button>
                    )}
                  </div>

                  {verdict?.agreed === false && (
                    <div
                      style={{
                        flexBasis: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>Nível correto:</span>
                      {levelOptions.map((l) => (
                        <button
                          key={l}
                          type="button"
                          data-testid={`review-level-${r.axisId}-${l}`}
                          onClick={() => setCorrect(r, l)}
                          title={levelLabel(l, levels.names)}
                          style={{
                            ...DM,
                            fontSize: 12,
                            fontWeight: 700,
                            width: 34,
                            height: 30,
                            borderRadius: 8,
                            cursor: "pointer",
                            background: verdict.correctedLevel === l ? C.ink : C.white,
                            color: verdict.correctedLevel === l ? C.white : C.ink,
                            border: `1px solid ${verdict.correctedLevel === l ? C.ink : C.border}`,
                          }}
                        >
                          L{l}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...UI, fontSize: 12.5, color: C.ink50 }}>
              {reviewedCount} de {rows.length} competências revisadas
            </span>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || reviewedCount === 0}
              data-testid="review-submit"
              style={{
                ...DM,
                fontWeight: 700,
                fontSize: 13,
                padding: "9px 18px",
                borderRadius: 999,
                cursor: submitting || reviewedCount === 0 ? "not-allowed" : "pointer",
                background: reviewedCount === 0 ? C.ink20 : C.ink,
                color: C.white,
                border: "none",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Salvando…" : `Salvar revisão (${reviewedCount})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function VerdictButton({
  children,
  icon: Icon,
  active,
  disabled = false,
  onClick,
  testId,
  title,
}: {
  children: React.ReactNode;
  icon: typeof Check;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      title={title}
      style={{
        ...DM,
        fontWeight: 600,
        fontSize: 12.5,
        padding: "6px 12px",
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        background: active ? C.ink : C.white,
        color: active ? C.white : disabled ? C.ink20 : C.ink,
        border: `1px solid ${active ? C.ink : C.border}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Icon size={13} /> {children}
    </button>
  );
}
